import { useState, useMemo, useCallback } from 'react'

import { getNextUnprocessedComment } from '~/modules/post/utils'
import { useTrackCommentSelected } from '~/shared/hooks/tracking'
import type { Comment } from '~/gql/graphql.ts'

type trackingDataProps = {
  post?: string
  usedHotKey?: boolean
}

type useCommentSelectionProps = {
  selectedComment: Comment | null
  actions: {
    selectComment: (
      comment: Comment | null,
      trackingData?: trackingDataProps,
    ) => void
    selectNext: (
      comments: Comment[],
      currComment?: Comment | null,
      trackingData?: trackingDataProps,
    ) => void
    selectPrev: (
      comments: Comment[],
      currComment: Comment | null,
      trackingData?: trackingDataProps,
    ) => void
  }
}

/**
 *
 */
export default function (): useCommentSelectionProps {
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null)
  const trackCommentSelected = useTrackCommentSelected()

  const selectComment = useCallback(
    (comment: Comment | null, trackingData: trackingDataProps = {}): void => {
      trackCommentSelection(comment, trackingData)
      setSelectedComment(comment)
    },
    [],
  )

  const selectNext = useCallback(
    (
      comments: Comment[],
      currComment?: Comment | null,
      trackingData: trackingDataProps = {},
    ) => {
      const next = getNextUnprocessedComment(comments, currComment)
      trackCommentSelection(next, trackingData)
      setSelectedComment(next)
    },
    [],
  )

  const selectPrev = useCallback(
    (
      comments: Comment[],
      currComment: Comment | null,
      trackingData: trackingDataProps = {},
    ) => {
      const prev = getNextUnprocessedComment(comments, currComment, false)
      trackCommentSelection(prev, trackingData)
      setSelectedComment(prev)
    },
    [],
  )

  const trackCommentSelection = useCallback(
    (comment: Comment | null, trackingData: trackingDataProps = {}) => {
      const { post, usedHotKey = false } = trackingData

      if (!comment || !post) {
        return null
      }

      trackCommentSelected(post, comment, { usedHotKey })
    },
    [],
  )

  const actions = useMemo(
    () => ({
      selectComment,
      selectPrev,
      selectNext,
    }),
    [selectComment, selectPrev, selectNext],
  )

  return { selectedComment, actions }
}
